import React, { useState } from 'react';
import {
  Box, Link, Dialog, DialogTitle, DialogContent, IconButton, Typography, Tooltip,
} from '@mui/material';
import { Close, Place } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';

// Simple in-memory cache so we don't reverse-geocode the same point repeatedly.
const placeCache = new Map<string, string>();

const roundKey = (lat: number, lng: number) => `${lat.toFixed(5)},${lng.toFixed(5)}`;

/**
 * Reverse-geocode a coordinate to a human-readable address using the Google
 * Geocoding API. Falls back to the raw coordinates if the API is unavailable
 * or not enabled for the configured key.
 */
async function reverseGeocode(lat: number, lng: number): Promise<string> {
  const key = roundKey(lat, lng);
  if (placeCache.has(key)) return placeCache.get(key)!;
  const coords = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    placeCache.set(key, coords);
    return coords;
  }
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
    );
    const data = await res.json();
    const address = data?.results?.[0]?.formatted_address;
    const value = address || coords;
    placeCache.set(key, value);
    return value;
  } catch {
    placeCache.set(key, coords);
    return coords;
  }
}

interface LocationViewerProps {
  lat: number | null | undefined;
  lng: number | null | undefined;
  /** Optional title for the map dialog (e.g. "Check-in location"). */
  title?: string;
  /** When true, eagerly resolve the place name. Otherwise shows coordinates as a link. */
  showPlaceName?: boolean;
}

/**
 * Renders a clickable location that opens an exact-GPS map (no range/radius
 * circle). Optionally reverse-geocodes the coordinate to a readable place name.
 */
const LocationViewer: React.FC<LocationViewerProps> = ({ lat, lng, title, showPlaceName = true }) => {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<string>('');

  const hasCoords = lat != null && lng != null;
  const numLat = Number(lat);
  const numLng = Number(lng);
  const coords = hasCoords ? `${numLat.toFixed(5)}, ${numLng.toFixed(5)}` : '';

  // Resolve the place name lazily on first render (or when coordinates change).
  React.useEffect(() => {
    let cancelled = false;
    if (!hasCoords || !showPlaceName) return;
    reverseGeocode(numLat, numLng).then((p) => { if (!cancelled) setPlace(p); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numLat, numLng, hasCoords, showPlaceName]);

  if (!hasCoords) {
    return <Typography variant="body2" color="text.secondary">—</Typography>;
  }

  const label = showPlaceName ? (place || coords) : coords;

  return (
    <>
      <Box display="flex" alignItems="center" gap={0.5} sx={{ maxWidth: 260 }}>
        <Place fontSize="small" color="error" />
        <Tooltip title={t('attendance.location.viewMap')}>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="hover"
            onClick={() => setOpen(true)}
            sx={{ textAlign: 'start', lineHeight: 1.2 }}
          >
            {label}
          </Link>
        </Tooltip>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title || t('attendance.location.title')}
          <IconButton onClick={() => setOpen(false)} sx={{ color: 'error.main' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {showPlaceName && place ? place : coords}
          </Typography>
          <iframe
            title="exact-location"
            width="100%"
            height="320"
            style={{ border: 0, borderRadius: 8 }}
            loading="lazy"
            src={`https://www.google.com/maps?q=${numLat},${numLng}&z=17&output=embed`}
          />
          <Box mt={1}>
            <Link
              href={`https://www.google.com/maps?q=${numLat},${numLng}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
            >
              {t('attendance.location.openInMaps')}
            </Link>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LocationViewer;
